import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Copy, Loader2, CheckCircle2, XCircle, ShieldCheck, ChevronDown, BookOpen, FileDown, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SendSheetDialog, TokenCheckHistory } from "@/components/IntegrationInfoExtras";
import { MonthlySheetRow } from "@/components/MonthlySheetRow";

const copyTxt = (t) => { navigator.clipboard.writeText(t); toast.success("Copiato"); };

function Field({ label, value, mono = true, testid }) {
  return (
    <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2 min-w-0" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
      <div className="flex items-center gap-2">
        <span className={`text-xs text-slate-200 break-all flex-1 ${mono ? "font-mono2" : ""}`}>{value || "—"}</span>
        {value && mono && <button type="button" onClick={() => copyTxt(String(value))} className="text-slate-500 hover:text-slate-100 shrink-0"><Copy className="h-3.5 w-3.5" /></button>}
      </div>
    </div>
  );
}

const Ok = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 ${ok ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#EF4444]/15 text-[#FCA5A5]"}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} {children}
  </span>
);

const fmtDt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

function TokenCheckRow({ tc, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/whatsapp/token-check");
      onRefresh(data.token_check);
      setTick((t) => t + 1);
      toast.success(data.token_check?.level === "ok" ? "Token controllato: tutto regolare" : "Controllo eseguito: vedi segnalazioni");
    } catch (e) { toast.error(e.response?.data?.detail || "Controllo non riuscito"); }
    finally { setBusy(false); }
  };
  const lvl = tc?.level;
  const color = lvl === "ok" ? "#10B981" : lvl === "warn" ? "#F59E0B" : lvl === "ko" ? "#EF4444" : "#64748B";
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="info-token-check">
      <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color, boxShadow: `0 0 6px ${color}` }} />
      <span className="text-slate-400">Ultimo controllo automatico: {tc?.checked_at ? <span className="text-slate-200" data-testid="info-token-check-date">{fmtDt(tc.checked_at)}</span> : <span className="text-slate-500">mai eseguito</span>}
        {tc?.checked_at && <> · <span style={{ color }} data-testid="info-token-check-level">{lvl === "ok" ? "tutto regolare" : lvl === "warn" ? "attenzione" : "problema"}</span></>}</span>
      <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="info-token-check-now" className="h-6 px-2 text-[11px] border-[#1F2937] text-slate-300 ml-auto">
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Controlla ora
      </Button>
      {tc?.issues?.length > 0 && <ul className="w-full list-disc pl-5 space-y-0.5" data-testid="info-token-check-issues">{tc.issues.map((t, i) => <li key={i} style={{ color }}>{t}</li>)}</ul>}
      {tc?.notes?.length > 0 && <ul className="w-full list-disc pl-5 space-y-0.5 text-slate-500" data-testid="info-token-check-notes">{tc.notes.map((t, i) => <li key={i}>{t}</li>)}</ul>}
      <p className="w-full text-[10px] text-slate-500">Controllo ogni giorno alle 06:00 UTC. L'amministratore riceve un'email solo quando lo stato cambia.</p>
      <div className="w-full"><TokenCheckHistory refreshKey={tick} /></div>
    </div>
  );
}

export function IntegrationInfoPanel() {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  useEffect(() => {
    if (!open || d) return;
    api.get("/whatsapp/integration-info").then((r) => setD(r.data)).catch(() => setD({ error: true }));
  }, [open, d]);
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get("/whatsapp/integration-info/pdf", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const name = (cd.match(/filename="?([^";]+)"?/) || [])[1] || `scheda-integrazione-meta-${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Scheda tecnica scaricata");
    } catch { toast.error("Errore nella generazione del PDF"); } finally { setPdfBusy(false); }
  };
  const tok = d?.token;
  const wh = d?.webhook;
  return (
    <section className="bg-[#111827]/80 border border-[#1F2937] rounded-2xl p-5 space-y-4" data-testid="integration-info-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 text-left" data-testid="integration-info-toggle">
        <div className="flex items-center gap-2">
          <Info className="h-5 w-5 text-[#10B981]" />
          <h2 className="text-lg font-semibold text-slate-100">Informazioni sull'integrazione</h2>
        </div>
        <span className="text-xs text-slate-500 flex items-center gap-1">App Meta · WABA · API · permessi <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></span>
      </button>
      {open && (!d ? <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-500" /></div> : d.error ? <p className="text-sm text-[#FCA5A5]">Informazioni non disponibili.</p> : (
        <div className="space-y-4" data-testid="integration-info-body">
          <p className="text-sm text-slate-400">Questa piattaforma è un CRM/marketing proprietario integrato con <b className="text-slate-200">{d.platform}</b>: la connessione è diretta tra l'app Meta sotto indicata e il tuo WhatsApp Business Account, senza provider intermedi.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <Field label="App ID (Meta for Developers)" value={d.app_id} testid="info-app-id" />
            <Field label="WABA ID (WhatsApp Business Account)" value={d.waba_id} testid="info-waba-id" />
            <Field label="Phone Number ID" value={d.phone_number_id} testid="info-phone-id" />
            <Field label="Versione Graph API" value={d.graph_api_version} testid="info-api-version" />
            <Field label="Nome account WABA" value={d.waba?.name} mono={false} testid="info-waba-name" />
            <Field label="Numero / nome verificato" value={d.phone?.display_phone_number ? `${d.phone.display_phone_number} · ${d.phone.verified_name || ""}` : d.phone?.error} mono={false} testid="info-phone" />
            <Field label="Stato verifica attività" value={d.waba?.business_verification_status || d.waba?.account_review_status} mono={false} testid="info-verification" />
            <Field label="URL pubblico (link traccianti)" value={d.public_url} testid="info-public-url" />
            <Field label="Webhook callback" value={d.webhook_url} testid="info-webhook-url" />
          </div>
          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2 space-y-2" data-testid="info-token">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Token di accesso e permessi</p>
            {tok && tok.checked === false ? <p className="text-xs text-[#FCD34D]" data-testid="info-token-inconclusive">Verifica non conclusiva: {tok.error || "Meta non ha risposto"}. Il token non risulta revocato.</p> : tok ? (
              <div className="flex flex-wrap gap-1.5">
                <Ok ok={tok.valid}>{tok.valid ? "token valido" : "token non valido"}</Ok>
                <Ok ok={tok.permanent}>{tok.permanent ? "permanente (System User)" : tok.expires_at ? `scade il ${new Date(tok.expires_at * 1000).toLocaleDateString("it-IT")}` : "scadenza sconosciuta"}</Ok>
                {tok.type && <span className="text-[11px] rounded-full px-2 py-0.5 bg-slate-700/40 text-slate-300">tipo {tok.type}</span>}
                {tok.app_id && (tok.app_match
                  ? <Ok ok>{`generato dall'app ${tok.app_id}`}</Ok>
                  : <span className="text-[11px] rounded-full px-2 py-0.5 bg-slate-700/40 text-slate-300" data-testid="info-token-app-note">generato dall'app {tok.app_id}</span>)}
              </div>
            ) : <p className="text-xs text-slate-500">WhatsApp non configurato.</p>}
            {tok?.app_id && !tok.app_match && <p className="text-[11px] text-slate-500" data-testid="info-token-app-mismatch">Nota: il token proviene dall'app Meta {tok.app_id}, diversa da WHATSAPP_APP_ID ({d.app_id}). Tutto funziona regolarmente; per coerenza, quando vuoi, genera il token dall'app {d.app_id} (manuale cap. 11.3).</p>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
              {d.required_scopes.map((s) => {
                const has = tok?.scopes?.includes(s.scope);
                return (
                  <div key={s.scope} className={`rounded-md border px-2 py-1.5 ${has ? "border-[#10B981]/40" : tok?.scopes?.length ? "border-[#F59E0B]/40" : "border-[#1F2937]"}`} data-testid={`info-scope-${s.scope}`}>
                    <p className="text-[11px] font-mono2 text-slate-200 flex items-center gap-1">{has ? <CheckCircle2 className="h-3 w-3 text-[#10B981]" /> : tok?.scopes?.length ? <XCircle className="h-3 w-3 text-[#F59E0B]" /> : null}{s.scope}</p>
                    <p className="text-[10px] text-slate-500">{s.purpose}</p>
                  </div>
                );
              })}
            </div>
            {tok?.scopes?.length > 0 && <p className="text-[10px] text-slate-500">Permessi presenti nel token: {tok.scopes.join(", ")}</p>}
            <div className="pt-2 border-t border-[#1F2937]/60"><TokenCheckRow tc={d.token_check} onRefresh={(tc) => setD({ ...d, token_check: tc })} /></div>
          </div>
          <div className="bg-[#0A0E17] border border-[#1F2937] rounded-lg px-3 py-2 space-y-1" data-testid="info-webhook">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Webhook e sicurezza</p>
            <div className="flex flex-wrap gap-1.5">
              <Ok ok={d.app_secret_set}>{d.app_secret_set ? "firma X-Hub-Signature-256 verificata" : "App Secret mancante: firma non verificata"}</Ok>
              {wh?.configured && !wh.error && <Ok ok={wh.callback_ok}>{wh.callback_ok ? "callback corretta su Meta" : "callback diversa su Meta"}</Ok>}
              {wh?.configured && !wh.error && <Ok ok={!(wh.missing_fields || []).length}>campi: {(wh.fields || []).join(", ") || "nessuno"}</Ok>}
              <span className="text-[11px] rounded-full px-2 py-0.5 bg-slate-700/40 text-slate-300">modalità campagne: {d.mode === "live" ? "LIVE" : "demo"}</span>
            </div>
          </div>
          <div className="space-y-1" data-testid="info-apis">
            <p className="text-[10px] uppercase tracking-wide text-slate-500">API Graph utilizzate</p>
            <div className="overflow-x-auto rounded-lg border border-[#1F2937]">
              <table className="w-full min-w-[520px] text-xs">
                <tbody>{d.apis_used.map((a) => (
                  <tr key={a.api} className="border-t border-[#1F2937] first:border-t-0"><td className="px-3 py-1.5 font-mono2 text-slate-200 whitespace-nowrap">{a.api}</td><td className="px-3 py-1.5 text-slate-400">{a.purpose}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={downloadPdf} disabled={pdfBusy} data-testid="info-download-pdf" className="bg-[#10B981] hover:bg-[#059669] text-[#0A0E17] font-semibold h-8">
              {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4 mr-1.5" />} Scarica scheda tecnica (PDF)
            </Button>
            <Button variant="outline" onClick={() => setSendOpen(true)} data-testid="info-send-email" className="h-8 border-[#1F2937] text-slate-200 bg-transparent hover:bg-[#1F2937]">
              <Mail className="h-4 w-4 mr-1.5" /> Invia via email
            </Button>
            <SendSheetDialog open={sendOpen} onClose={() => setSendOpen(false)} defaultEmail={d.default_email} workspaceName={d.workspace_name} />
            <span className="text-[11px] text-slate-500">Compilata in tempo reale, da allegare alla verifica attività Meta. Il token non compare mai.</span>
            <Link to="/manuale#cap-17" className="inline-flex items-center gap-1.5 text-xs text-[#10B981] hover:underline sm:ml-auto" data-testid="info-manual-link"><BookOpen className="h-3.5 w-3.5" /> Scheda completa nel Manuale (cap. 17)</Link>
          </div>
          <MonthlySheetRow info={d} onChange={(patch) => setD((prev) => ({ ...prev, ...patch }))} />
        </div>
      ))}
    </section>
  );
}
